/**
 * CSV import wizard for the rescue portal.
 *
 *   Step 1 — Get the template (download CSV, browse field list)
 *   Step 2 — Upload & review (dropzone → inline verification table)
 *   Step 3 — Track the import (live worker progress → final outcome)
 *
 * Parsing, validation, tooltips, icons, and the progress view live in
 * sibling modules so this file stays focused on the wizard flow + Apollo
 * wiring.
 *
 * Submit hands the CSV off to S3 via a backend-issued presigned PUT URL.
 * A worker then imports the rows asynchronously; step 3 polls
 * `rescueCsvImportProgress` and shows the per-row outcome (imported /
 * skipped) when the job finishes. The in-flight import id is saved to a
 * cookie so the user can close the wizard and resume tracking on reopen.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, useLazyQuery, useQuery } from '@apollo/client';
import Modal, { ModalTitle, ModalSubtitle } from '../../../components/Modal/Modal';
import StepProgress from '../../../components/StepProgress/StepProgress';
import useChipClaimEligibility from '../../../hooks/useChipClaimEligibility';
import useImportProgress from '../../../hooks/useImportProgress';
import { readImportJobKey } from '../../../lib/importJobCookie';
import { allBreedsQuery, type AllBreedsData } from '../../../lib/rescueQueries';
import {
  rescueGetImportUploadUrlQuery,
  type RescueGetImportUploadUrlData,
  type RescueImportContentType,
} from '../../../lib/integrationQueries';
import { Species } from '../../../../types/gql-op-types';
import {
  trackImportAbandoned,
  trackImportFailed,
  trackImportFileParsed,
  trackImportFileRejected,
  trackImportProcessingCompleted,
  trackImportProcessingFailed,
  trackImportPublished,
  trackImportWizardOpened,
  type ImportFileParsedProps,
} from '../../../analytics';
import styles from '../Integrations.module.scss';
import { type CsvHeader, isCsvHeader, MAX_FILE_BYTES, MAX_ROWS, REQUIRED_HEADERS, formatBytes } from './constants';
import { CsvParseError, parseCsv } from './parseCsv';
import { displayRowNumber, type ParsedRow, validateAndMapRow, type ValidationError } from './validateRow';
import TemplateIntro from './TemplateIntro';
import UploadAndReview from './UploadAndReview';
import ImportProgress from './ImportProgress';

// MIME type sent on the S3 PUT. Must match what the presigned URL was
// signed for, or S3 rejects the request with SignatureDoesNotMatch.
const CSV_CONTENT_TYPE = 'text/csv';
// Enum the upload-URL query expects — the backend maps `TEXT_CSV` to the
// `text/csv` MIME type above when signing the URL.
const CSV_UPLOAD_CONTENT_TYPE: RescueImportContentType = 'TEXT_CSV';

/**
 * Categorize an upload-flow error for analytics. The upload now has two
 * potential failure surfaces — fetching the presigned URL (Apollo) and
 * the S3 PUT itself (fetch) — so we tag each distinctly.
 *
 *   url_fetch_failed       — Apollo failed to deliver the presigned URL (network/auth/server)
 *   graphql_<code>         — server returned `extensions.code` on the URL query
 *   graphql_error          — server returned a GraphQL error with no code
 *   s3_put_status_<code>   — PUT completed with a non-2xx HTTP status
 *   <error_constructor>    — non-Apollo Error from client code, including a PUT
 *                            network failure (e.g. `TypeError` from fetch)
 *   unknown                — non-Error throw
 */
function categorizeUploadError(err: unknown): string {
  if (err instanceof ApolloError) {
    if (err.networkError) {
      return 'url_fetch_failed';
    }
    const code = err.graphQLErrors[0]?.extensions?.code;
    if (typeof code === 'string' && code.length > 0) {
      return `graphql_${code.toLowerCase()}`;
    }
    return 'graphql_error';
  }
  if (err instanceof Error) {
    return err.name || 'error';
  }
  return 'unknown';
}

interface ImportPetsModalProps {
  open: boolean;
  onClose: () => void;
  /** Fires when the CSV upload to S3 has succeeded. A backend worker
   * performs the actual import asynchronously, so this is "received, not
   * yet processed" — parent shouldn't refetch chip lists or stats. */
  onSuccess: () => void;
}

export default function ImportPetsModal({ open, onClose, onSuccess }: ImportPetsModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isDragging, setIsDragging] = useState(false);
  const [fileSizeBytes, setFileSizeBytes] = useState<number | null>(null);
  const [filterMode, setFilterMode] = useState<'all' | 'errors'>('all');
  // Raw CSV text — kept in state so it can be PUT to S3 once the
  // backend hands us a presigned URL. Not used for the parsing path
  // (which works on the already-parsed `parsedRows`).
  const [originalCsv, setOriginalCsv] = useState<string>('');
  const [fileName, setFileName] = useState<string | null>(null);
  // `parseError` covers upload-time failures (size cap, malformed CSV,
  // unknown columns). `submitError` covers failures from the
  // upload-URL fetch or the S3 PUT — separated so the two render in
  // different slots and don't conflate.
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  // True while the S3 PUT is in flight. Loading state for the URL
  // query comes from Apollo; this covers the fetch() phase only.
  const [putInFlight, setPutInFlight] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Sequence token for in-flight `handleFile` reads. Bumping invalidates
  // any earlier read that's still mid-async — fixes rapid re-pick races.
  const fileSeqRef = useRef(0);
  // Tracks whether the user actually uploaded a batch this session.
  // Used by the close handler to skip "abandoned" analytics on the
  // step-3 success view.
  const uploadedRef = useRef(false);

  // `no-cache` so a second submit always gets a freshly signed URL
  // instead of replaying a cached one whose `expiration` has elapsed.
  const [getUploadUrl, { loading: gettingUrl }] = useLazyQuery<RescueGetImportUploadUrlData>(
    rescueGetImportUploadUrlQuery,
    { fetchPolicy: 'no-cache' },
  );
  const uploading = gettingUrl || putInFlight;

  // Post-upload tracking: hands the uploaded object off to the worker via
  // `rescueStartImport` and polls `rescueCsvImportProgress` until the job
  // reaches a terminal state. Drives the step-3 progress view.
  const {
    phase: importPhase,
    progress: importProgressData,
    errorMessage: importErrorMessage,
    pollError: importPollError,
    start: startImport,
    resume: resumeImport,
    importMissing,
    reset: resetImportProgress,
  } = useImportProgress();
  // One-shot guard so the success/failure analytics event fires once per
  // import, not on every poll. Re-armed by `resetWorkingState`.
  const outcomeTrackedRef = useRef(false);

  // Fetch the breed list for breed-name validation, one query per species.
  //
  // The backend's `allBreeds(species)` resolver defaults a null/omitted
  // species to DOG — it does NOT return every species — so a single
  // un-parameterized query would only ever load dog breeds, and every
  // cat/other row that carries a breed would be false-flagged as "not a
  // recognized breed". We query each species explicitly and merge so a
  // mixed-species CSV validates against the right breed set. Cache-first;
  // each per-species query is shared with PetFormModal / RegisterChipModal.
  //
  // The combined `breedsError` is surfaced so the verifier doesn't
  // false-flag every breed-bearing row when a query failed (an empty
  // `breedNameMap` would otherwise match every breed against an empty set).
  // Publish is gated on it for the same reason.
  const {
    data: dogBreeds,
    loading: dogLoading,
    error: dogError,
    refetch: dogRefetch,
  } = useQuery<AllBreedsData>(allBreedsQuery, {
    variables: { species: Species.DOG },
    fetchPolicy: 'cache-first',
    skip: !open,
  });
  const {
    data: catBreeds,
    loading: catLoading,
    error: catError,
    refetch: catRefetch,
  } = useQuery<AllBreedsData>(allBreedsQuery, {
    variables: { species: Species.CAT },
    fetchPolicy: 'cache-first',
    skip: !open,
  });
  const {
    data: otherBreeds,
    loading: otherLoading,
    error: otherError,
    refetch: otherRefetch,
  } = useQuery<AllBreedsData>(allBreedsQuery, {
    variables: { species: Species.OTHER },
    fetchPolicy: 'cache-first',
    skip: !open,
  });
  const breedsLoading = dogLoading || catLoading || otherLoading;
  const breedsError = dogError ?? catError ?? otherError;
  const refetchBreeds = useCallback(
    () => Promise.all([dogRefetch(), catRefetch(), otherRefetch()]),
    [dogRefetch, catRefetch, otherRefetch],
  );
  // Lower-cased name → set of species the breed is registered under. Used
  // both to confirm "is this a real breed?" and to enforce species/breed
  // consistency when the row specifies both. Value type is `Set<Species>`
  // (the enum) — the API hands back species as a free-form string, but
  // every valid row stores one of the enum tokens.
  const breedNameMap = useMemo(() => {
    const map = new Map<string, Set<Species>>();
    const allBreeds = [
      ...(dogBreeds?.allBreeds ?? []),
      ...(catBreeds?.allBreeds ?? []),
      ...(otherBreeds?.allBreeds ?? []),
    ];
    for (const b of allBreeds) {
      const key = b.name.trim().toLowerCase();
      if (!key) {
        continue;
      }
      const set = map.get(key) ?? new Set<Species>();
      set.add(b.species as Species);
      map.set(key, set);
    }
    return map;
  }, [dogBreeds, catBreeds, otherBreeds]);

  // Every chip serial in the CSV, deduplicated and uppercased. Fed to
  // `useChipClaimEligibility` to classify each as already-mine, claimable
  // (the import will register it to this rescue), or owned-by-another
  // (rejected). One bulk call replaces what used to be a paginate-every-
  // owned-chip + per-unowned-serial-lookup combo.
  const allChipSerials = useMemo<readonly string[]>(() => {
    if (parsedRows.length === 0) {
      return [];
    }
    const set = new Set<string>();
    for (const r of parsedRows) {
      const key = r.chipSerialNumber.toUpperCase();
      if (key) {
        set.add(key);
      }
    }
    return [...set];
  }, [parsedRows]);
  const {
    results: claimEligibility,
    loading: claimEligibilityLoading,
    error: claimEligibilityError,
    refetch: refetchClaimEligibility,
  } = useChipClaimEligibility(allChipSerials, !open);

  // Reset every piece of working state EXCEPT the step token. Called from
  // open, close, and "Upload another" so adding a new field requires
  // touching one place. Step transitions are caller-specific (open → 1,
  // upload-another → 2) and stay at the call site.
  function resetWorkingState() {
    setFileName(null);
    setFileSizeBytes(null);
    setParseError(null);
    setSubmitError(null);
    setParsedRows([]);
    setIsDragging(false);
    setFilterMode('all');
    setOriginalCsv('');
    // Stop any in-flight progress polling and re-arm outcome analytics —
    // every reset path (open, close, "upload another") starts fresh.
    resetImportProgress();
    outcomeTrackedRef.current = false;
  }

  useEffect(() => {
    if (open) {
      // Re-arm abandonment tracking for this session before deciding the entry
      // step (the resume branch below flips it back off).
      uploadedRef.current = false;
      resetWorkingState();
      // If a recently-started import is still saved (cookie, <30 min), resume
      // its progress view instead of starting a new upload. The job runs
      // server-side regardless of this wizard, so "check back" lands here.
      const resumableImportId = readImportJobKey();
      if (resumableImportId) {
        resumeImport(resumableImportId);
        setStep(3);
        // The upload already happened in a prior session, so closing this
        // resumed tracker isn't an "abandoned" upload.
        uploadedRef.current = true;
      } else {
        setStep(1);
      }
      trackImportWizardOpened();
    } else {
      // Modal closed — defense-in-depth in case the parent keeps this
      // component mounted with `open: false` indefinitely. Open also
      // resets, but we don't want owner PII (email/phone/address) to
      // sit in React memory between sessions.
      resetWorkingState();
    }
    // resetWorkingState is stable for our purposes — it captures setters
    // which never change identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A resumed import the server can no longer find (gone / aged out): abandon
  // the resume and drop back to the upload step. The hook has already cleared
  // the saved cookie, so this won't loop on the next open.
  useEffect(() => {
    if (importMissing) {
      resetImportProgress();
      setStep(1);
    }
  }, [importMissing, resetImportProgress]);

  // Fire the import-outcome analytics event exactly once when the worker
  // job settles. `outcomeTrackedRef` guards against re-firing on every
  // poll, and `resetWorkingState` re-arms it for the next import.
  useEffect(() => {
    if (outcomeTrackedRef.current) {
      return;
    }
    if (importPhase === 'succeeded') {
      outcomeTrackedRef.current = true;
      trackImportProcessingCompleted({
        total_rows: importProgressData?.totalRows ?? null,
        final_processed: importProgressData?.finalProcessed ?? null,
        final_failed: importProgressData?.finalFailed ?? null,
      });
    } else if (importPhase === 'failed') {
      outcomeTrackedRef.current = true;
      const status = importProgressData?.status;
      // Categorize without leaking the raw `terminalError` text (PII rule).
      let reason: string;
      if (status === 'FAILED') {
        reason = 'failed';
      } else {
        // No status row → the `rescueStartImport` hand-off itself errored.
        reason = 'start_error';
      }
      trackImportProcessingFailed(reason);
    }
  }, [importPhase, importProgressData]);

  // Cross-row checks are applied here rather than in `validateAndMapRow`
  // because they depend on context the row-level validator doesn't have:
  // (a) the full set of CSV rows for duplicate-detection, and (b) the
  // rescue's owned chip serials for ownership verification.
  const verifiedRows = useMemo<ParsedRow[]>(() => {
    if (parsedRows.length === 0) {
      return parsedRows;
    }
    const seen = new Map<string, number[]>();
    for (const r of parsedRows) {
      const key = r.chipSerialNumber.toUpperCase();
      if (!key) {
        continue;
      }
      const list = seen.get(key);
      if (list) {
        list.push(r.rowIndex);
      } else {
        seen.set(key, [r.rowIndex]);
      }
    }
    return parsedRows.map((r) => {
      const extra: ValidationError[] = [];
      const key = r.chipSerialNumber.toUpperCase();
      const dupes = key ? (seen.get(key) ?? []) : [];
      if (dupes.length > 1) {
        const others = dupes.filter((idx) => idx !== r.rowIndex).map(displayRowNumber);
        extra.push({
          field: 'microchip_id',
          code: 'duplicate',
          message: `microchip_id duplicates row${others.length > 1 ? 's' : ''} ${others.join(', ')}`,
        });
      }
      // Chip resolution: five-way decision per chip, driven by the
      // bulk eligibility lookup.
      //   `in_my_inventory` → existing unattached chip; pet will register
      //                       against it.
      //   `claim`           → chip will be registered to this rescue.
      //   `other_reseller`  → ERROR; cannot claim.
      //   `already_linked`  → ERROR; chip is already attached to a pet,
      //                       backend's pre-flight would reject.
      //   `not_usable`      → ERROR; chip exists but its condition
      //                       (recalled, damaged, etc.) blocks import.
      //   undefined         → lookup still pending; `verifying` gates
      //                       Publish so this won't be submitted early.
      if (key) {
        const status = claimEligibility.get(key);
        if (status === 'other_reseller') {
          extra.push({
            field: 'microchip_id',
            code: 'owned_by_other_reseller',
            message: 'microchip_id is already registered to another reseller',
          });
        } else if (status === 'already_linked') {
          extra.push({
            field: 'microchip_id',
            code: 'already_linked',
            message: 'microchip_id is already attached to a pet',
          });
        } else if (status === 'not_usable') {
          extra.push({
            field: 'microchip_id',
            code: 'not_usable',
            message: 'microchip_id is not in a condition that allows import',
          });
        }
      }
      // Breed check: the backend resolves breed name → breed id, so the name
      // has to match an entry in `allBreeds`. When species is also supplied,
      // the breed must be registered under that species (a "Labrador" row
      // with species=CAT is rejected). Gated on `breedsLoading` AND
      // `breedsError`: when the breeds query itself failed, `breedNameMap`
      // is empty and would false-flag every breed; we surface the error
      // separately and gate Publish on it instead. An empty `breedNameMap`
      // after a successful (non-erroring) load still means "checked" and
      // correctly rejects unknown breeds.
      if (r.breed && !breedsLoading && !breedsError) {
        const breedKey = r.breed.trim().toLowerCase();
        const speciesSet = breedNameMap.get(breedKey);
        if (!speciesSet) {
          extra.push({
            field: 'breed',
            code: 'unknown',
            message: `breed "${r.breed}" is not a recognized breed`,
          });
        } else if (r.species && !speciesSet.has(r.species as Species)) {
          extra.push({
            field: 'breed',
            code: 'invalid_format',
            message: `breed "${r.breed}" is not valid for species ${r.species}`,
          });
        }
      }
      if (extra.length === 0) {
        return r;
      }
      return { ...r, rawErrors: [...r.rawErrors, ...extra] };
    });
  }, [parsedRows, claimEligibility, breedNameMap, breedsLoading, breedsError]);

  const validRowCount = useMemo(() => verifiedRows.filter((r) => r.rawErrors.length === 0).length, [verifiedRows]);
  const invalidRowCount = verifiedRows.length - validRowCount;
  const duplicateRowCount = useMemo(
    () => verifiedRows.filter((r) => r.rawErrors.some((e) => e.code === 'duplicate')).length,
    [verifiedRows],
  );
  // Rows whose chip will be CLAIMED (not just re-linked) during import.
  // Shown in the summary so the rescue knows publishing will assign new
  // chips to their account.
  const claimRowCount = useMemo(() => {
    let n = 0;
    for (const r of verifiedRows) {
      if (r.rawErrors.length > 0) {
        continue;
      }
      const key = r.chipSerialNumber.toUpperCase();
      if (key && claimEligibility.get(key) === 'claim') {
        n += 1;
      }
    }
    return n;
  }, [verifiedRows, claimEligibility]);

  const displayedRows = useMemo(
    () => (filterMode === 'errors' ? verifiedRows.filter((r) => r.rawErrors.length > 0) : verifiedRows),
    [verifiedRows, filterMode],
  );

  function rejectFile(reason: string, message: string) {
    setParseError(message);
    setParsedRows([]);
    trackImportFileRejected(reason);
  }

  async function handleFile(file: File) {
    // Tag this run. A later `handleFile` bumps the ref, and we bail out at
    // every async boundary if our token is stale — preventing a slow read
    // of file A from clobbering state already advanced by file B.
    const seq = ++fileSeqRef.current;
    const isStale = () => fileSeqRef.current !== seq;

    setParseError(null);
    setFileName(file.name);
    setFileSizeBytes(file.size);
    setFilterMode('all');
    // Extension check up front — the `<input accept=".csv">` hint is
    // ignored by drag-and-drop and is a hint anyway, so the user can
    // hand us anything. Reject by name extension (case-insensitive)
    // rather than MIME type because `file.type` is unreliable across
    // platforms (often empty, sometimes `application/vnd.ms-excel`).
    if (!/\.csv$/i.test(file.name)) {
      const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '';
      const got = ext ? `a ${ext} file` : 'a file with no extension';
      rejectFile('wrong_file_type', `Only .csv files can be imported — you uploaded ${got} (${file.name}).`);
      return;
    }
    // Hard size cap BEFORE we read the file into memory — a multi-MB CSV
    // would block the main thread on `file.text()` and can OOM the tab.
    if (file.size > MAX_FILE_BYTES) {
      rejectFile(
        'size_exceeded',
        `File too large — limit is ${formatBytes(MAX_FILE_BYTES)} (got ${formatBytes(file.size)})`,
      );
      return;
    }
    try {
      const text = await file.text();
      if (isStale()) {
        return;
      }
      const matrix = parseCsv(text);
      if (matrix.length < 2) {
        rejectFile('empty_csv', 'CSV is empty or only has a header row');
        return;
      }
      const headerRow = matrix[0].map((h) => h.trim());
      const unknown = headerRow.filter((h) => !isCsvHeader(h));
      if (unknown.length > 0) {
        rejectFile('unknown_column', `Unknown column${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}`);
        return;
      }
      // Duplicate-header check: `headers.indexOf(key)` in the row mapper
      // picks the first match and silently drops every later column with
      // the same name. Reject upfront so two `microchip_id` columns
      // can't quietly discard data.
      const duplicates = Array.from(new Set(headerRow.filter((h, i) => headerRow.indexOf(h) !== i)));
      if (duplicates.length > 0) {
        rejectFile('duplicate_column', `Duplicate column${duplicates.length > 1 ? 's' : ''}: ${duplicates.join(', ')}`);
        return;
      }
      const missing = REQUIRED_HEADERS.filter((h) => !headerRow.includes(h));
      if (missing.length > 0) {
        rejectFile(
          'missing_required_column',
          `Missing required column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`,
        );
        return;
      }
      if (matrix.length - 1 > MAX_ROWS) {
        rejectFile('row_count_exceeded', `Too many rows — limit is ${MAX_ROWS} per upload (got ${matrix.length - 1})`);
        return;
      }
      // Stored rowIndex is the file line number (header is line 1, so
      // data rows start at 2). The user-facing label is rendered via
      // `displayRowNumber(rowIndex)` so the review table reads as
      // 1-based data rows.
      const rows = matrix.slice(1).map((cells, i) => validateAndMapRow(headerRow as CsvHeader[], cells, i + 2));
      // Stash the original CSV only after all validation passes — we
      // PUT it to S3 on submit, so there's no reason to hold it
      // during a rejected upload.
      setOriginalCsv(text);
      setParsedRows(rows);
      // Stay on step 2 — the verification table renders inline once rows are
      // parsed. No separate "verify" step.
      // Aggregate error codes (counts only — no PII) for analytics.
      // Duplicate detection has to happen here, not in the per-row
      // validator: duplicates are a cross-row concern, and the `verifiedRows`
      // memo that attaches duplicate errors only runs after this event has
      // already fired. Mirror its seen-map pass so valid_count and
      // duplicate_count line up with what the user actually sees in the
      // review table.
      const serialCounts = new Map<string, number>();
      for (const row of rows) {
        const key = row.chipSerialNumber.toUpperCase();
        if (!key) {
          continue;
        }
        serialCounts.set(key, (serialCounts.get(key) ?? 0) + 1);
      }
      const codeCounts: Record<string, number> = {};
      let validCount = 0;
      let dupCount = 0;
      for (const row of rows) {
        const key = row.chipSerialNumber.toUpperCase();
        const isDup = key !== '' && (serialCounts.get(key) ?? 0) > 1;
        if (isDup) {
          dupCount += 1;
        }
        if (row.rawErrors.length === 0 && !isDup) {
          validCount += 1;
        }
        for (const e of row.rawErrors) {
          codeCounts[e.code] = (codeCounts[e.code] ?? 0) + 1;
        }
      }
      if (dupCount > 0) {
        codeCounts.duplicate = (codeCounts.duplicate ?? 0) + dupCount;
      }
      const parsedProps: ImportFileParsedProps = {
        row_count: rows.length,
        valid_count: validCount,
        invalid_count: rows.length - validCount,
        duplicate_count: dupCount,
        file_size_bytes: file.size,
        error_codes: Object.keys(codeCounts).length > 0 ? codeCounts : undefined,
      };
      trackImportFileParsed(parsedProps);
    } catch (err) {
      if (isStale()) {
        return;
      }
      if (err instanceof CsvParseError) {
        rejectFile('malformed_csv', err.message);
      } else {
        rejectFile('read_error', err instanceof Error ? err.message : 'Failed to read file');
      }
    }
  }

  async function handleConfirm() {
    // Frontend gate: refuse to submit a batch with any client-side
    // validation errors, or while verification queries are still in
    // flight / failed. The button is already disabled in these states
    // — this is belt-and-suspenders against a programmatic call
    // sneaking through (e.g. someone wiring up an Enter shortcut
    // later). The worker on the backend runs its own validation; this
    // guard is purely about not uploading a known-bad set.
    if (publishDisabled) {
      return;
    }
    if (verifiedRows.length === 0 || verifiedRows.some((r) => r.rawErrors.length > 0)) {
      return;
    }
    if (!originalCsv) {
      setSubmitError('No file contents to upload');
      trackImportFailed('missing_file_contents');
      return;
    }
    setSubmitError(null);
    try {
      // Step 1: ask the backend for a presigned PUT URL. `fetchPolicy:
      // 'no-cache'` ensures we don't replay a previously-cached URL
      // whose expiration has elapsed.
      const { data, error } = await getUploadUrl({ variables: { input: { contentType: CSV_UPLOAD_CONTENT_TYPE } } });
      if (error) {
        throw error;
      }
      const upload = data?.rescueGetImportUploadUrl;
      if (!upload?.url) {
        setSubmitError('Server did not return an upload URL');
        trackImportFailed('empty_upload_url');
        return;
      }

      // Step 2: PUT the CSV bytes directly to S3. The signed URL signs
      // only `host`, so the request must not carry any `x-amz-*` header
      // the signature doesn't cover — S3 rejects an unsigned `x-amz-*`
      // header with 403. `Content-Type` is not an `x-amz-*` header, so
      // S3 allows it unsigned.
      setPutInFlight(true);
      let putRes: Response;
      try {
        putRes = await fetch(upload.url, {
          method: 'PUT',
          headers: { 'Content-Type': CSV_CONTENT_TYPE },
          body: new Blob([originalCsv], { type: CSV_CONTENT_TYPE }),
        });
      } finally {
        setPutInFlight(false);
      }
      if (!putRes.ok) {
        setSubmitError(`Upload failed (HTTP ${putRes.status})`);
        trackImportFailed(`s3_put_status_${putRes.status}`);
        return;
      }

      // Worker takes over from here. Hand the uploaded object off via
      // `rescueStartImport` and begin polling — the step-3 view tracks
      // the job to completion. Fire-and-forget: `startImport` flips its
      // own `starting` state synchronously and never throws (it captures
      // failures into `importPhase`), so the step-3 view renders the
      // in-flight spinner immediately and surfaces any error itself.
      void startImport(upload.key);
      // Clear PII out of React state now that the bytes are in S3.
      setOriginalCsv('');
      setParsedRows([]);
      setStep(3);
      uploadedRef.current = true;
      trackImportPublished({
        row_count: verifiedRows.length,
        claim_count: claimRowCount,
      });
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setSubmitError(message);
      trackImportFailed(categorizeUploadError(err));
    }
  }

  const stepLabels: Record<1 | 2 | 3, string> = {
    1: 'Get the template',
    2: 'Upload & review',
    3: 'Done',
  };

  const hasParsed = parsedRows.length > 0;
  // `verifying` gates the Publish button while either verification
  // query is still in flight — breeds or per-chip claim eligibility.
  // Once both settle, the row-level errors are authoritative.
  const verifying = breedsLoading || claimEligibilityLoading;

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  }

  function handleUploadAnother() {
    // Reset everything except the breed/chip caches (they're tied to the
    // open Apollo session) and bounce back to the upload step so the user
    // can drop the next file.
    setStep(2);
    resetWorkingState();
    // Re-arm abandonment tracking for the next upload attempt.
    uploadedRef.current = false;
  }

  // Compute disable + label for the Upload button. We block submit when
  // any row error remains AND when either the breed list OR the chip
  // eligibility lookup failed to load — running validation against an
  // empty breed map false-flags every row with a breed, and silently
  // claiming through a failed eligibility check lets bad rows reach the
  // worker (which would reject them silently downstream).
  const breedsBroken = !!breedsError;
  const publishDisabled =
    uploading ||
    verifying ||
    breedsBroken ||
    claimEligibilityError ||
    !hasParsed ||
    invalidRowCount > 0 ||
    validRowCount === 0;
  let publishLabel: string;
  if (uploading) {
    publishLabel = 'Uploading…';
  } else if (verifying) {
    publishLabel = 'Verifying…';
  } else if (breedsBroken) {
    publishLabel = "Couldn't verify breeds";
  } else if (claimEligibilityError) {
    publishLabel = "Couldn't verify chips";
  } else if (!hasParsed) {
    publishLabel = 'Upload';
  } else if (invalidRowCount > 0) {
    publishLabel = `Fix ${invalidRowCount} error${invalidRowCount === 1 ? '' : 's'} to upload`;
  } else {
    publishLabel = `Upload ${validRowCount} pet${validRowCount === 1 ? '' : 's'}`;
  }

  // Gates ALL close paths while an upload is in flight. The Modal
  // component routes backdrop click and Escape through `onClose` —
  // without this, the user could close mid-PUT and leave the file
  // half-uploaded with no UI confirmation of the eventual outcome.
  // Also fires the abandonment analytics event for closes that didn't
  // upload anything.
  function safeClose() {
    if (uploading) {
      return;
    }
    if (!uploadedRef.current) {
      trackImportAbandoned(step, parsedRows.length > 0);
    }
    onClose();
  }

  return (
    <Modal open={open} onClose={safeClose} size="xx-wide" showCloseX={!uploading} dismissOnBackdropClick={false}>
      <ModalTitle>
        {step === 1 && 'Import existing registrations'}
        {step === 2 && 'Upload & review'}
        {step === 3 && <span className={styles.srOnly}>Upload received</span>}
      </ModalTitle>
      {step === 1 && (
        <ModalSubtitle>Bring over pets you already have in Clinic HQ, ShelterLuv, or a spreadsheet.</ModalSubtitle>
      )}
      {step === 2 && (
        <ModalSubtitle>
          {hasParsed ? (
            <>
              {verifiedRows.length} row{verifiedRows.length === 1 ? '' : 's'} from <strong>{fileName}</strong>
            </>
          ) : (
            <>Upload the template you filled in &mdash; we&rsquo;ll check every row.</>
          )}
        </ModalSubtitle>
      )}
      <StepProgress
        currentStep={step}
        totalSteps={3}
        labelSuffix={step === 3 ? undefined : stepLabels[step]}
        labelOverride={step === 3 ? 'Done' : undefined}
      />

      {step === 1 && <TemplateIntro onCancel={safeClose} onNext={() => setStep(2)} />}

      {step === 2 && (
        <UploadAndReview
          fileInputRef={fileInputRef}
          fileName={fileName}
          fileSizeBytes={fileSizeBytes}
          isDragging={isDragging}
          setIsDragging={setIsDragging}
          handleFile={handleFile}
          handleDrop={handleDrop}
          onReplaceFile={resetWorkingState}
          hasParsed={hasParsed}
          parseError={parseError}
          submitError={submitError}
          breedsBroken={breedsBroken}
          refetchBreeds={refetchBreeds}
          eligibilityBroken={claimEligibilityError}
          refetchEligibility={refetchClaimEligibility}
          verifiedRows={verifiedRows}
          displayedRows={displayedRows}
          validRowCount={validRowCount}
          invalidRowCount={invalidRowCount}
          duplicateRowCount={duplicateRowCount}
          claimRowCount={claimRowCount}
          verifying={verifying}
          uploading={uploading}
          publishDisabled={publishDisabled}
          publishLabel={publishLabel}
          filterMode={filterMode}
          setFilterMode={setFilterMode}
          onBack={() => setStep(1)}
          onPublish={handleConfirm}
        />
      )}

      {step === 3 && (
        <ImportProgress
          phase={importPhase}
          progress={importProgressData}
          errorMessage={importErrorMessage}
          pollError={importPollError}
          fileName={fileName}
          onClose={safeClose}
          onUploadAnother={handleUploadAnother}
        />
      )}

      {uploading && (
        <div className={styles.processingOverlay} role="status" aria-live="polite" aria-busy="true">
          <div className={styles.processingOverlayInner}>
            <svg
              className={styles.processingSpinner}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" opacity="0.25" />
              <path d="M12 2a10 10 0 0 1 10 10" />
            </svg>
            <div className={styles.processingLabel}>
              Uploading {validRowCount} pet{validRowCount === 1 ? '' : 's'}…
            </div>
            <div className={styles.processingSubLabel}>
              This can take a moment — please don&rsquo;t close the window.
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
